import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Button, Stack, Typography } from '@mui/material';
import CloudSyncOutlinedIcon from '@mui/icons-material/CloudSyncOutlined';
import { useAuth } from '../../auth/googleDomainAuth.jsx';
import {
  useBillingReconciliationCatalog,
  useBillingReconciliationFileHealth,
  useBillingReconciliationFileHealthSummary,
  useBillingReconciliationFullEvidence,
  useRefreshBillingReconciliationBacContext,
  useResetBillingReconciliationVarianceReviews,
  useBillingReconciliationSuggestedMatches,
  usePromoteBillingReconciliationActionToRecurringRule,
} from '../../hooks/api/useBillingReconciliationHooks.js';
import { useBillingReconciliationOptimizationSettings } from '../../hooks/api/useBillingReconciliationSettingsHooks.js';
import { logger } from '../../utils/logger.js';
import ResizableDrawerShell from './ResizableDrawerShell.jsx';
import VarianceDrawerBody from './VarianceDrawerBody.jsx';
import {
  CURRENT_USER,
  PRODUCT_STATUS_CONFIG,
  normalizeProductStatus,
} from './constants.js';
import {
  evidenceTotals,
  nextReviewPayload,
  normalizeEvidenceRowsForDrawer,
} from './varianceDrawerModel.js';
import { VARIANCE_ACTIONS } from './varianceActions.js';
import { useResizableDrawerWidth } from './useResizableDrawerWidth.js';
import { useDrawerMode } from './useDrawerMode.js';
import { compareProductCodesForLane, productFamilyKey } from './DrawerEvidenceHelpers.jsx';
import { contextWithForcedEvidence } from './forcedEvidence.js';
import {
  buildVarianceActionFromEvidenceDrop,
  buildVarianceActionFromRemoveDrop,
} from './evidenceDropAction.js';
import { applyVarianceActionTotals } from './varianceActionTotals.js';
import { draftAddsForFile } from './varianceActionPreview.js';
import { useVarianceDrawerNotes } from './useVarianceDrawerNotes.js';
import { useVarianceDrawerActions } from './useVarianceDrawerActions.js';
import { useVarianceDrawerDpeActions } from './useVarianceDrawerDpeActions.js';
import { useVarianceDrawerReviewFields } from './useVarianceDrawerReviewFields.js';
import { useVarianceDrawerMockSources } from './useVarianceDrawerMockSources.js';
import { useVarianceDrawerKeyboardShortcuts } from './useVarianceDrawerKeyboardShortcuts.js';
import { applyNoteTemplate, noteQuality } from './noteTemplates.js';
import { buildVarianceCompletionState } from './varianceCompletionReadiness.js';
import { hasReviewableVarianceTarget, reviewTargetFromDrawerRow } from './varianceReviewTarget.js';
import { withAuditLinkToken } from './investigationFeedHelpers.js';
import { buildDpeActionDraftFromContext } from './dpeActionModel.js';
import { prefetchBacContextWindow } from './bacContextPrefetch.js';
import { capabilitiesForProgram } from './programCapabilities.js';
import {
  applyCpqInheritanceOverrides,
  cpqInheritanceOverrideKey,
} from './cpqInheritanceOverrides.js';
import {
  ACTIVE_CONTEXT_WARM_STATUSES,
  drawerEvidencePending,
  drawerEvidencePendingReason,
  normalizedWarmStatus,
  refreshCompleteNotice,
  shouldResolveQueuedRefreshNotice,
  warmFingerprint,
} from './varianceDrawerWarmState.js';

export { drawerEvidencePending } from './varianceDrawerWarmState.js';

export { normalizeEvidenceRowsForDrawer } from './varianceDrawerModel.js';
export { isCompletionBlockingAction } from './varianceCompletionReadiness.js';
export const shouldLoadVarianceDrawerCatalog = ({
  draftVarianceAction,
  draftDpeAction,
  open,
  program,
} = {}) => Boolean((draftVarianceAction || draftDpeAction) && open && program);
const normalizeProgramKey = (value = '') => String(value || '').trim().toLowerCase();
const getDrawerEvidenceDurationMs = (bac) => {
  if (!bac || typeof performance === 'undefined' || typeof performance.getEntriesByName !== 'function') return null;
  const entries = performance.getEntriesByName(`billing.drawer.${bac}.open_to_evidence`);
  const latest = entries?.[entries.length - 1];
  const duration = Number(latest?.duration);
  return Number.isFinite(duration) ? Math.round(duration) : null;
};

export const drawerPrefetchBacs = (navigation = {}) => {
  const normalizeTarget = (value) => {
    if (value && typeof value === 'object') {
      const bac = String(value.bac || '').trim();
      if (!bac) return null;
      return {
        bac,
        varianceId: value.varianceId || value.id || null,
      };
    }
    const bac = String(value || '').trim();
    return bac ? { bac, varianceId: null } : null;
  };
  if (Array.isArray(navigation?.prefetchBacs) && navigation.prefetchBacs.length) {
    return navigation.prefetchBacs.map(normalizeTarget).filter(Boolean);
  }
  return [navigation?.nextBac, navigation?.previousBac]
    .map(normalizeTarget)
    .filter(Boolean);
};

export const dpeActionsAllowedForProgram = (program) => {
  const normalized = normalizeProgramKey(program || 'website');
  return Boolean(capabilitiesForProgram(normalized).dpeActions);
};
const dialogContextFromDropAction = (action = {}, fallbackContext = {}) => ({
  ...fallbackContext,
  source: action.source || fallbackContext.source,
  sourceLabel: action.sourceLabel || fallbackContext.sourceLabel,
  productName: action.productName || fallbackContext.productName,
  productCode: action.productCode || fallbackContext.productCode,
  price: action.price ?? fallbackContext.price,
  status: action.productStatus || fallbackContext.status,
  replacementProductCode: action.replacementProductCode || fallbackContext.replacementProductCode || '',
  proposedPrice: action.proposedPrice ?? fallbackContext.proposedPrice ?? '',
  orderItemId: action.manualOrderItemId || fallbackContext.orderItemId,
  referenceId: action.manualReferenceId || fallbackContext.referenceId,
  salesforceAccountId: action.salesforceAccountId || fallbackContext.salesforceAccountId,
  salesforceAccountName: action.salesforceAccountName || fallbackContext.salesforceAccountName,
  bindingMethod: action.bindingMethod || fallbackContext.bindingMethod,
  bindingConfidence: action.bindingConfidence || fallbackContext.bindingConfidence,
  sourceRowKey: action.sourceRowKey || fallbackContext.sourceRowKey,
  row: fallbackContext.row,
});

const readySectionData = (section) => (section?.status === 'ready' ? section.data : null);
const sectionQuery = ({ data, fallback, isError = false }) => (
  data !== null && data !== undefined
    ? { data, isLoading: false, isFetching: false, isError: false, isSuccess: true }
    : { ...fallback, isError: Boolean(isError || fallback?.isError) }
);

const VarianceDrawer = ({
  open,
  onClose,
  row,
  review,
  onReviewChange,
  saveReview,
  saveReviewAsync = null,
  setNotes,
  runId,
  serviceMonth = null,
  importMode = null,
  mockMode = false,
  context = null,
  navigation = null,
}) => {
  const { user } = useAuth() || {};
  const queryClient = useQueryClient();
  const currentUserName = user?.name || user?.email || CURRENT_USER.name;
  const [historyEntry, setHistoryEntry] = useState(null);
  const mockSources = useVarianceDrawerMockSources({ mockMode, bac: row?.bac });
  const { drawerWidth, isResizing, resetWidth, startResize } = useResizableDrawerWidth();
  const { drawerMode, setDrawerMode, toggleFullscreen } = useDrawerMode();
  const promoteRecurringRule = usePromoteBillingReconciliationActionToRecurringRule();
  const focusProductCode = context?.productCode || context?.replacementProductCode || '';
  const [varianceMenuState, setVarianceMenuState] = useState(null);
  const [draftVarianceAction, setDraftVarianceAction] = useState(null);
  const [draftDpeAction, setDraftDpeAction] = useState(null);
  const [productDetailContext, setProductDetailContext] = useState(null);
  const [recurringPromotionActionId, setRecurringPromotionActionId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [lowerPanelsReady, setLowerPanelsReady] = useState(false);
  const [closeAfterActionIdle, setCloseAfterActionIdle] = useState(false);
  const [refreshNotice, setRefreshNotice] = useState(null);
  const [refreshObservedActive, setRefreshObservedActive] = useState(false);
  const [refreshBaselineFingerprint, setRefreshBaselineFingerprint] = useState(null);
  const [cpqInheritanceOverrides, setCpqInheritanceOverrides] = useState({});
  const allowDpeActions = dpeActionsAllowedForProgram(row?.program || context?.program || 'website')
    && !row?.review?.inheritedResolved;
  const {
    category,
    handleMarkReviewed,
    remediation,
    setCategory,
    setRemediation,
  } = useVarianceDrawerReviewFields({
    row,
    review,
    saveReview,
    onReviewChange,
  });
  useEffect(() => {
    setVarianceMenuState(null);
    setDraftVarianceAction(null);
    setDraftDpeAction(null);
    setProductDetailContext(null);
    setLowerPanelsReady(false);
    setCloseAfterActionIdle(false);
    setRefreshNotice(null);
    setRefreshObservedActive(false);
    setRefreshBaselineFingerprint(null);
    setCpqInheritanceOverrides({});
  }, [row?.bac]);
  const {
    noteDraft,
    activeDraftNoteId,
    setNoteDraft,
    scheduleNoteSave,
    sealCurrentDraft,
    handleEditNote,
    handleDeleteNote,
  } = useVarianceDrawerNotes({
    row,
    review,
    setNotes,
    onReviewChange,
    currentUserName,
  });
  const handleApplyNoteTemplate = useCallback((templateId, templateContext = {}) => {
    const templateText = applyNoteTemplate({ templateId, row, context: templateContext });
    const next = templateContext.auditActionId ? withAuditLinkToken(templateText, templateContext.auditActionId) : templateText;
    if (noteQuality(next).status === 'ready') {
      sealCurrentDraft(next);
      return;
    }
    setNoteDraft(next);
    scheduleNoteSave(next);
  }, [row, scheduleNoteSave, sealCurrentDraft, setNoteDraft]);
  const rowEvidenceQuery = useBillingReconciliationFullEvidence(
    { runId, bac: row?.bac, varianceId: row?.id || row?.varianceId || null, limit: 500, importMode, contextMode: 'critical' },
    { enabled: open && !mockMode && Boolean(runId && row?.bac) },
  );
  const operatorBundle = rowEvidenceQuery.data?.operatorBundle || null;
  const bundleFileHealthSnapshotReady = operatorBundle?.fileHealth?.snapshotReady === true
    || operatorBundle?.fileHealth?.readiness?.fileHealthSnapshotReady === true;
  const bundleFileHealthRowsStatus = operatorBundle?.fileHealth?.rowsStatus
    || (operatorBundle?.fileHealth?.status === 'ready' ? 'ready' : null);
  const bundleFileHealthSummaryStatus = operatorBundle?.fileHealth?.summaryStatus
    || (operatorBundle?.fileHealth?.status === 'ready' ? 'ready' : null);
  const bundleFileHealthRows = (bundleFileHealthRowsStatus === 'ready' || bundleFileHealthSnapshotReady)
    ? operatorBundle.fileHealth.rows || []
    : null;
  const bundleFileHealthSummary = (bundleFileHealthSummaryStatus === 'ready' || bundleFileHealthSnapshotReady)
    ? operatorBundle.fileHealth.summary || null
    : null;
  const bundleLifecycleAlerts = readySectionData(operatorBundle?.lifecycleAlerts);
  const bundleDpeActions = readySectionData(operatorBundle?.dpeActions);
  const bundleSuggestedMatches = readySectionData(operatorBundle?.suggestedMatches);
  const refreshBacContext = useRefreshBillingReconciliationBacContext();
  const optimizationSettingsQuery = useBillingReconciliationOptimizationSettings({
    enabled: open && !mockMode,
  });
  const bacContextPrefetchEnabled = optimizationSettingsQuery.data?.bacContextPrefetchEnabled !== false;
  const operatorBundlePrefetchCount = Number(optimizationSettingsQuery.data?.operatorBundlePrefetchCount || 3);
  const navigationPrefetchBacsKey = Array.isArray(navigation?.prefetchBacs)
    ? navigation.prefetchBacs.map((target) => {
      if (target && typeof target === 'object') return `${target.bac || ''}:${target.varianceId || target.id || ''}`;
      return String(target || '').trim();
    }).join('|')
    : '';
  const prefetchBacs = useMemo(() => {
    if (navigationPrefetchBacsKey) {
      return drawerPrefetchBacs(navigation);
    }
    return drawerPrefetchBacs({ nextBac: navigation?.nextBac, previousBac: navigation?.previousBac });
  }, [navigation, navigationPrefetchBacsKey]);
  const prefetchBacsKey = prefetchBacs
    .map((target) => `${target.bac || ''}:${target.varianceId || target.id || ''}`)
    .join('|');
  const lastPrefetchKeyRef = useRef(null);
  useEffect(() => {
    if (!open || !row?.bac || typeof performance === 'undefined' || typeof performance.mark !== 'function') return;
    performance.mark(`billing.drawer.${row.bac}.open`);
  }, [open, row?.bac]);
  useEffect(() => {
    if (!open || !row?.bac || !rowEvidenceQuery.isSuccess || typeof performance === 'undefined' || typeof performance.mark !== 'function') return;
    performance.mark(`billing.drawer.${row.bac}.evidence_ready`);
    if (typeof performance.measure === 'function') {
      try {
        performance.measure(
          `billing.drawer.${row.bac}.open_to_evidence`,
          `billing.drawer.${row.bac}.open`,
          `billing.drawer.${row.bac}.evidence_ready`,
        );
        const durationMs = getDrawerEvidenceDurationMs(row.bac);
        const thresholdMs = Number(optimizationSettingsQuery.data?.billingSlowLogThresholdMs || 5000);
        if (optimizationSettingsQuery.data?.billingFrontendPerfLogEnabled && durationMs !== null && durationMs >= thresholdMs) {
          logger.warn('Billing drawer evidence load exceeded threshold', {
            event: 'billing.drawer.evidence.slow',
            runId,
            bac: row.bac,
            program: row.program || context?.program || null,
            durationMs,
            thresholdMs,
            contextMode: 'critical',
          });
        }
      } catch (_error) {
        // Browser marks are opportunistic diagnostics only.
      }
    }
  }, [context?.program, open, optimizationSettingsQuery.data, row?.bac, row?.program, rowEvidenceQuery.isSuccess, runId]);
  useEffect(() => {
    if (!open || mockMode || !rowEvidenceQuery.isSuccess) return undefined;
    if (typeof requestIdleCallback === 'function') {
      const handle = requestIdleCallback(() => setLowerPanelsReady(true), { timeout: 800 });
      return () => {
        if (typeof cancelIdleCallback === 'function') cancelIdleCallback(handle);
      };
    }
    const handle = setTimeout(() => setLowerPanelsReady(true), 120);
    return () => clearTimeout(handle);
  }, [mockMode, open, rowEvidenceQuery.isSuccess, row?.bac]);
  useEffect(() => {
    if (!open || mockMode || !runId || importMode === 'historical_backfill' || !rowEvidenceQuery.isSuccess) return;
    if (navigation?.currentBac && row?.bac && String(navigation.currentBac) !== String(row.bac)) return;
    if (!prefetchBacsKey) return;
    const requestKey = [runId, row?.bac || '', importMode || '', prefetchBacsKey].join('|');
    if (lastPrefetchKeyRef.current === requestKey) return;
    lastPrefetchKeyRef.current = requestKey;
    let cancelled = false;
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    prefetchBacContextWindow({
      queryClient,
      runId,
      enabled: bacContextPrefetchEnabled,
      activeBac: row?.bac,
      importMode,
      prefetchBacs,
      limit: 500,
      windowSize: Number.isFinite(operatorBundlePrefetchCount) ? operatorBundlePrefetchCount : 5,
      isCancelled: () => cancelled,
      signal: controller?.signal,
    }).catch((error) => {
      if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.message === 'canceled') return;
      console.warn('[BillingReconciliation] BAC context prefetch failed:', error.message);
    });
    return () => {
      cancelled = true;
      controller?.abort();
    };
  }, [
    bacContextPrefetchEnabled,
    importMode,
    mockMode,
    navigation?.currentBac,
    operatorBundlePrefetchCount,
    open,
    prefetchBacs,
    prefetchBacsKey,
    queryClient,
    row?.bac,
    rowEvidenceQuery.isSuccess,
    runId,
  ]);
  const fileHealthQuery = useBillingReconciliationFileHealth(
    {
      runId,
      bac: row?.bac,
      limit: context?.source === 'file_health' ? 500 : 50,
      openOnly: context?.source !== 'file_health',
    },
    { enabled: open && !mockMode && lowerPanelsReady && Boolean(runId && row?.bac) && !bundleFileHealthSnapshotReady && !bundleFileHealthRows },
  );
  const fileHealthSummaryQuery = useBillingReconciliationFileHealthSummary(
    { runId, bac: row?.bac },
    { enabled: open && !mockMode && lowerPanelsReady && Boolean(runId && row?.bac) && !bundleFileHealthSnapshotReady && !bundleFileHealthSummary },
  );
  const recurringSuggestionsQuery = useBillingReconciliationSuggestedMatches(
    { runId, bac: row?.bac },
    { enabled: open && !mockMode && lowerPanelsReady && Boolean(runId && row?.bac) && !bundleSuggestedMatches },
  );
  const mergedFileHealthQuery = sectionQuery({
    data: bundleFileHealthRows,
    fallback: fileHealthQuery,
    isError: operatorBundle?.fileHealth?.status === 'error',
  });
  const mergedFileHealthSummaryQuery = sectionQuery({
    data: bundleFileHealthSummary,
    fallback: fileHealthSummaryQuery,
    isError: operatorBundle?.fileHealth?.status === 'error',
  });
  const mergedRecurringSuggestionsQuery = sectionQuery({
    data: bundleSuggestedMatches || null,
    fallback: recurringSuggestionsQuery,
    isError: operatorBundle?.suggestedMatches?.status === 'error',
  });
  const recurringSuggestions = mergedRecurringSuggestionsQuery.data?.rows || [];
  const catalogQuery = useBillingReconciliationCatalog(
    { program: row?.program || 'website', limit: 500 },
    { enabled: shouldLoadVarianceDrawerCatalog({ draftVarianceAction, draftDpeAction, open, program: row?.program || 'website' }) },
  );
  const reopenReviewAfterActionChange = useCallback(() => {
    if (!row || !review?.reviewed) return;
    const next = saveReview(row, { reviewed: false });
    onReviewChange(row.bac, next);
  }, [onReviewChange, review?.reviewed, row, saveReview]);
  const {
    varianceActions,
    varianceActionError,
    setVarianceActionError,
    persistVarianceAction,
    patchVarianceActionFields,
    removeVarianceAction,
    resetAllVarianceActions,
    saveAllVarianceActions,
    shouldAcceptDropAction,
    isVarianceActionSaveBusy,
    isVarianceActionBusy,
  } = useVarianceDrawerActions({
    row,
    runId,
    open,
    mockMode,
    currentUserName,
    onActionsChangedAfterCompletion: reopenReviewAfterActionChange,
    prefetchedActions: rowEvidenceQuery.data?.workflow?.actions || null,
  });
  const resetVarianceReviews = useResetBillingReconciliationVarianceReviews();
  const {
    dpeActions,
    dpeActionError,
    persistDpeAction,
    removeDpeAction,
    isDpeActionBusy,
  } = useVarianceDrawerDpeActions({
    row,
    runId,
    open,
    mockMode,
    enabled: lowerPanelsReady,
    prefetchedActions: bundleDpeActions,
  });

  const handleOpenVarianceMenu = useCallback((event, varianceContext) => {
    if (!varianceContext) return;
    event.preventDefault?.();
    event.stopPropagation?.();
    setVarianceMenuState({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
      context: contextWithForcedEvidence(varianceContext),
    });
  }, []);
  const handleSaveVarianceAction = useCallback((action) => {
    persistVarianceAction(action).catch((error) => {
      console.error('[BillingReconciliation] Failed to persist variance action:', error);
    });
    setDraftVarianceAction(null);
  }, [persistVarianceAction]);
  const handleEvidenceDrop = useCallback((sourceContext, targetContext) => {
    if (!sourceContext || !targetContext) return;
    if (sourceContext.source === targetContext.source && sourceContext.productCode === targetContext.productCode) return;
    const action = buildVarianceActionFromEvidenceDrop({ sourceContext, targetContext, currentUserName });
    if (!action) return;
    if (!shouldAcceptDropAction(action, { reserve: false })) return;
    const dialogContext = action.actionType === VARIANCE_ACTIONS.ADD_TO_GM || action.actionType === VARIANCE_ACTIONS.ADD_TO_CPQ
      ? dialogContextFromDropAction(action, sourceContext)
      : dialogContextFromDropAction(action, targetContext);
    setDraftVarianceAction({
      actionType: action.actionType,
      context: dialogContext,
      prefillAction: action,
    });
  }, [currentUserName, shouldAcceptDropAction]);
  const handleEvidenceRemoveDrop = useCallback((sourceContext, removeTarget) => {
    const action = buildVarianceActionFromRemoveDrop({ sourceContext, removeTarget, currentUserName });
    if (!action) return;
    if (!shouldAcceptDropAction(action, { reserve: false })) return;
    setDraftVarianceAction({
      actionType: action.actionType,
      context: dialogContextFromDropAction(action, sourceContext),
      prefillAction: action,
    });
  }, [currentUserName, shouldAcceptDropAction]);
  const openDpeActionDraft = useCallback((actionType, sourceContext, targetContext = {}) => {
    if (!allowDpeActions || !sourceContext || !row) return;
    setDraftDpeAction(buildDpeActionDraftFromContext({
      actionType,
      context: sourceContext,
      targetContext,
      row: { ...row, runId },
      serviceMonth: row.serviceMonth || context?.serviceMonth,
      currentUserName,
    }));
  }, [allowDpeActions, context?.serviceMonth, currentUserName, row, runId]);
  const handleDpeActionDrop = useCallback((sourceContext, targetContext) => {
    if (!allowDpeActions || sourceContext?.source !== 'appsheet') return;
    openDpeActionDraft('add', sourceContext, targetContext);
  }, [allowDpeActions, openDpeActionDraft]);
  const handleDpeActionRemoveDrop = useCallback((sourceContext) => {
    if (!allowDpeActions || sourceContext?.source !== 'dpe') return;
    openDpeActionDraft('remove', sourceContext, { source: 'dpe', removeTarget: true });
  }, [allowDpeActions, openDpeActionDraft]);
  const openDpeActionEdit = useCallback((action) => {
    if (!allowDpeActions || !action?.id) return;
    setDraftDpeAction({
      ...action,
      actionType: action.actionType || 'add',
      vmtPayload: { ...(action.vmtPayload || {}) },
      metadata: {
        ...(action.metadata || {}),
        editedFrom: 'billing_dpe_action_modal',
      },
    });
  }, [allowDpeActions]);
  const handleSaveDpeAction = useCallback(async (action) => {
    try {
      await persistDpeAction(action);
      setDraftDpeAction(null);
    } catch (error) {
      console.error('[BillingReconciliation] Failed to persist DPE action:', error);
    }
  }, [persistDpeAction]);
  const handleSelectVarianceAction = useCallback((actionType, varianceContext) => {
    setVarianceMenuState(null);
    if (actionType === VARIANCE_ACTIONS.VIEW_DETAILS) {
      setProductDetailContext(varianceContext);
      return;
    }
    if (actionType === VARIANCE_ACTIONS.UNDO_CHANGE) {
      const action = varianceContext?.draftPreview?.action;
      const actionId = action?.id;
      if (!actionId) return;
      if (Object.prototype.hasOwnProperty.call(action, 'queueStatus')) removeDpeAction(actionId);
      else removeVarianceAction(actionId);
      return;
    }
    if (actionType === VARIANCE_ACTIONS.EDIT_CHANGE) {
      const action = varianceContext?.draftPreview?.action;
      if (!action?.id) return;
      if (Object.prototype.hasOwnProperty.call(action, 'queueStatus')) {
        openDpeActionEdit(action);
      } else {
        setDraftVarianceAction({
          actionType: action.actionType,
          context: varianceContext,
          prefillAction: action,
        });
      }
      return;
    }
    if (actionType === VARIANCE_ACTIONS.CLEAR_INHERITED_CPQ) {
      const overrideKey = cpqInheritanceOverrideKey(varianceContext);
      if (overrideKey) {
        setCpqInheritanceOverrides((current) => ({ ...current, [overrideKey]: true }));
      }
      return;
    }
    if (actionType === VARIANCE_ACTIONS.DPE_ADD_ACTION) {
      openDpeActionDraft('add', varianceContext, { source: 'dpe' });
      return;
    }
    if (actionType === VARIANCE_ACTIONS.DPE_CANCEL_ACTION) {
      openDpeActionDraft('remove', varianceContext, { source: 'dpe', removeTarget: true });
      return;
    }
    if (actionType === VARIANCE_ACTIONS.DPE_CHANGE_PRICE_ACTION) {
      openDpeActionDraft('change_price', varianceContext, { source: 'dpe' });
      return;
    }
    setDraftVarianceAction({ actionType, context: varianceContext });
  }, [openDpeActionDraft, openDpeActionEdit, removeDpeAction, removeVarianceAction]);
  const focusedAction = useMemo(() => {
    const focusedId = context?.focusActionId || context?.actionId;
    if (!focusedId) return null;
    return varianceActions.find((action) => String(action.id) === String(focusedId)) || null;
  }, [context?.actionId, context?.focusActionId, varianceActions]);
  const accountResolverContext = useMemo(() => {
    const fallbackAction = !focusedAction && context?.code === 'MISSING_SALESFORCE_ACCOUNT' && (context.actionId || context.focusActionId)
      ? context
      : null;
    const action = focusedAction || fallbackAction;
    if (!action) return null;
    const targetSource = action.targetSource || action.targetFile || action.source || action.sourceFile || (
      action.exportLane === 'cpq_peoplesoft_adjustment' || action.lane === 'cpq_peoplesoft_adjustment' ? 'cpq' : 'gm'
    );
    const productCode = action.replacementProductCode || action.customProductCode || action.productCode || action.sourceProductCode;
    return {
      source: targetSource,
      sourceLabel: action.targetLabel || action.sourceLabel || (targetSource === 'cpq' ? 'CPQ file' : 'GM file'),
      productName: action.productName || action.sourceProductName,
      productCode,
      price: action.proposedPrice ?? action.customPrice ?? action.originalPrice ?? action.price,
      status: action.productStatus || action.status,
      orderItemId: action.manualOrderItemId,
      referenceId: action.manualReferenceId,
      salesforceAccountId: action.salesforceAccountId,
      salesforceAccountName: action.salesforceAccountName,
      bindingMethod: action.bindingMethod,
      bindingConfidence: action.bindingConfidence,
      sourceRowKey: action.targetRowKey || action.sourceRowKey,
      resolverActionId: action.id || action.actionId || action.focusActionId,
      resolverActionType: action.actionType,
      row: {
        ...(action.targetEvidence || {}),
        ...(action.sourceEvidence || {}),
        bac: action.bac || row?.bac,
        sourceRowKey: action.targetRowKey || action.sourceRowKey,
        productName: action.productName || action.sourceProductName,
        productCode,
        dealerCost: action.originalPrice ?? action.price,
        salesforceAccountId: action.salesforceAccountId,
        salesforceAccountName: action.salesforceAccountName,
      },
    };
  }, [context, focusedAction, row?.bac]);
  const handleOpenAccountResolver = useCallback(() => {
    if (!accountResolverContext) return;
    setProductDetailContext(accountResolverContext);
  }, [accountResolverContext]);
  const handleApplyProductLink = useCallback((action) => {
    if (productDetailContext?.resolverActionId) {
      patchVarianceActionFields(productDetailContext.resolverActionId, {
        manualOrderItemId: action.manualOrderItemId,
        manualReferenceId: action.manualReferenceId,
        salesforceAccountId: action.salesforceAccountId,
        salesforceAccountName: action.salesforceAccountName,
        bindingMethod: action.bindingMethod,
        bindingConfidence: action.bindingConfidence,
        sourceEvidence: action.sourceEvidence,
        targetEvidence: action.targetEvidence,
        ...(productDetailContext.resolverActionType === VARIANCE_ACTIONS.ASSIGN_BAC ? {
          metadata: action.metadata,
          fix: action.fix,
        } : {}),
      }).catch((error) => {
        console.error('[BillingReconciliation] Failed to patch variance action account link:', error);
      });
      setProductDetailContext(null);
      return;
    }
    persistVarianceAction(action).catch((error) => {
      console.error('[BillingReconciliation] Failed to persist product link action:', error);
    });
    setProductDetailContext(null);
  }, [
    patchVarianceActionFields,
    persistVarianceAction,
    productDetailContext?.resolverActionId,
    productDetailContext?.resolverActionType,
  ]);
  const handlePromoteRecurring = useCallback((actionId) => {
    setRecurringPromotionActionId(actionId);
  }, []);
  const submitRecurringPromotion = useCallback((auditNote) => {
    if (!recurringPromotionActionId) return;
    promoteRecurringRule.mutate({ id: recurringPromotionActionId, auditNote });
    setRecurringPromotionActionId(null);
  }, [promoteRecurringRule, recurringPromotionActionId]);
  const evidenceRows = useMemo(() => {
    if (!rowEvidenceQuery.isSuccess) {
      return {
        gmLineItems: row?.gmLineItems || [],
        cpqLineItems: row?.cpqLineItems || [],
        dpeLineItems: row?.dpeLineItems || [],
        appsheetData: null,
        dpeData: null,
      };
    }
    return normalizeEvidenceRowsForDrawer(rowEvidenceQuery.data, row);
  }, [rowEvidenceQuery.isSuccess, rowEvidenceQuery.data, row]);
  const warmStatus = normalizedWarmStatus(row);
  const currentWarmFingerprint = warmFingerprint(row);
  const evidencePendingReason = drawerEvidencePendingReason({ evidenceRows, query: rowEvidenceQuery, row });
  const evidencePending = drawerEvidencePending({ evidenceRows, query: rowEvidenceQuery, row });
  useEffect(() => {
    if (!refreshNotice?.startsWith('Refresh queued')) return;
    if (ACTIVE_CONTEXT_WARM_STATUSES.has(warmStatus)) {
      setRefreshObservedActive(true);
      return;
    }
    if (shouldResolveQueuedRefreshNotice({
      warmStatus,
      baselineFingerprint: refreshBaselineFingerprint,
      currentFingerprint: currentWarmFingerprint,
      observedActive: refreshObservedActive,
    })) {
      setRefreshNotice(refreshCompleteNotice(warmStatus));
      setRefreshObservedActive(false);
      setRefreshBaselineFingerprint(null);
    }
  }, [currentWarmFingerprint, refreshBaselineFingerprint, refreshNotice, refreshObservedActive, warmStatus]);
  const displayCpqLineItems = useMemo(
    () => applyCpqInheritanceOverrides(evidenceRows.cpqLineItems || [], cpqInheritanceOverrides),
    [cpqInheritanceOverrides, evidenceRows.cpqLineItems],
  );
  const displayEvidenceRows = useMemo(() => ({
    ...evidenceRows,
    cpqLineItems: displayCpqLineItems,
  }), [displayCpqLineItems, evidenceRows]);
  const displayTotals = useMemo(() => {
    if (!rowEvidenceQuery.isSuccess && !row?.sourceContext) {
      return {
        gmTotal: row?.gmTotal || 0,
        cpqTotal: row?.cpqTotal || 0,
        dpeTotal: row?.dpeTotal || 0,
        deltas: row?.deltas || {},
      };
    }
    return applyVarianceActionTotals(evidenceTotals(displayEvidenceRows), varianceActions, {
      dpeActions,
      cpqRows: displayCpqLineItems,
    });
  }, [displayCpqLineItems, displayEvidenceRows, dpeActions, row, rowEvidenceQuery.isSuccess, varianceActions]);
  const billingProductFamilyLayout = useMemo(() => {
    const codes = [];
    const sourceCounts = [new Map(), new Map(), new Map(), new Map()];
    const add = (sourceIndex, code) => {
      const key = productFamilyKey(code);
      if (!key) return;
      if (code) codes.push(code);
      sourceCounts[sourceIndex].set(key, (sourceCounts[sourceIndex].get(key) || 0) + 1);
    };
    (evidenceRows.gmLineItems || []).filter((item) => item.isBilling).forEach((item) => add(0, item.productCode));
    draftAddsForFile({ actions: varianceActions, fileKind: 'gm' }).forEach((item) => add(0, item.productCode));
    const isBillingStatus = (status) => PRODUCT_STATUS_CONFIG[normalizeProductStatus(status, '')]?.billing;
    const comparisonItems = (evidenceRows.historicalFinalGmLineItems || []).length
      ? (evidenceRows.historicalFinalGmLineItems || []).filter((item) => item.isBilling).map((item) => ({ ...item, oemProductCode: item.productCode }))
      : displayCpqLineItems.filter((item) => isBillingStatus(item.productStatus));
    comparisonItems
      .forEach((item) => add(1, item.oemProductCode));
    draftAddsForFile({ actions: varianceActions, fileKind: 'cpq' }).forEach((item) => add(1, item.productCode));
    (evidenceRows.dpeLineItems || []).filter((item) => item.isBilling).forEach((item) => add(2, item.productCode));
    (evidenceRows.appsheetData?.orderItems || [])
      .filter((item) => isBillingStatus(item.status))
      .forEach((item) => add(3, item.productCode));
    const order = [...new Set(codes.sort(compareProductCodesForLane).map(productFamilyKey))];
    const minRows = order.reduce((acc, key) => {
      acc[key] = Math.max(1, ...sourceCounts.map((counts) => counts.get(key) || 0));
      return acc;
    }, {});
    return { order, minRows };
  }, [displayCpqLineItems, evidenceRows, varianceActions]);
  const {
    canMarkComplete,
    hasUnsavedVarianceActions,
    hasVarianceActions,
  } = useMemo(() => buildVarianceCompletionState({
    bac: row?.bac,
    mockMode,
    varianceActions,
    displayTotals,
    fileHealthSummary: mergedFileHealthSummaryQuery.data,
    fileHealthRows: mergedFileHealthQuery.isSuccess
      ? mergedFileHealthQuery.data
      : rowEvidenceQuery.data?.workflow?.fileHealth,
    fileHealthSummaryReady: mergedFileHealthSummaryQuery.isSuccess,
    fileHealthRowsReady: mergedFileHealthQuery.isSuccess || Array.isArray(rowEvidenceQuery.data?.workflow?.fileHealth),
    isVarianceActionBusy,
  }), [
    displayTotals,
    mergedFileHealthQuery.data,
    mergedFileHealthQuery.isSuccess,
    mergedFileHealthSummaryQuery.data,
    mergedFileHealthSummaryQuery.isSuccess,
    isVarianceActionBusy,
    mockMode,
    row?.bac,
    rowEvidenceQuery.data?.workflow?.fileHealth,
    varianceActions,
  ]);
  const reviewTargetRow = useMemo(
    () => reviewTargetFromDrawerRow(row, rowEvidenceQuery.data?.variance || {}),
    [row, rowEvidenceQuery.data?.variance],
  );
  const canMarkCompleteCurrent = canMarkComplete && hasReviewableVarianceTarget(reviewTargetRow, { mockMode });
  const safeClose = useCallback(() => {
    if (isVarianceActionBusy) {
      setCloseAfterActionIdle(true);
      return;
    }
    if (hasUnsavedVarianceActions) {
      setConfirmAction('close');
      return;
    }
    onClose?.();
  }, [hasUnsavedVarianceActions, isVarianceActionBusy, onClose]);
  useEffect(() => {
    if (!closeAfterActionIdle || isVarianceActionBusy) return;
    setCloseAfterActionIdle(false);
    if (hasUnsavedVarianceActions) {
      setConfirmAction('close');
      return;
    }
    onClose?.();
  }, [closeAfterActionIdle, hasUnsavedVarianceActions, isVarianceActionBusy, onClose]);
  const requestResetAllVarianceActions = useCallback(() => {
    if (!hasVarianceActions || isVarianceActionBusy) return;
    setConfirmAction('reset');
  }, [hasVarianceActions, isVarianceActionBusy]);

  const handleConfirmAction = useCallback(() => {
    if (confirmAction === 'close') {
      setConfirmAction(null);
      onClose?.();
      return;
    }
    if (confirmAction === 'reset') {
      setConfirmAction(null);
      resetAllVarianceActions();
    }
  }, [confirmAction, onClose, resetAllVarianceActions]);
  const handleMarkComplete = useCallback(async () => {
    if (!row || !canMarkCompleteCurrent) return;
    const patch = {
      reviewed: true,
      category: category || 'resolved',
      remediation: remediation || 'visual-builder-complete',
      resolveActionsOnComplete: true,
    };
    try {
      const next = saveReviewAsync ? await saveReviewAsync(reviewTargetRow, patch) : saveReview(reviewTargetRow, patch);
      onReviewChange(row.bac, next);
      navigation?.onNext?.();
    } catch (error) {
      if (!saveReviewAsync) throw error;
      // The review state owns the visible save error for async persistence failures.
    }
  }, [canMarkCompleteCurrent, category, navigation, onReviewChange, remediation, reviewTargetRow, row, saveReview, saveReviewAsync]);
  const handleResetReview = useCallback(async () => {
    const varianceId = reviewTargetRow?.id || reviewTargetRow?.varianceId || row?.id || row?.varianceId;
    if (!runId || !varianceId || resetVarianceReviews.isPending) return;
    try {
      await resetVarianceReviews.mutateAsync({ runId, varianceIds: [String(varianceId)] });
      onReviewChange(row.bac, { ...(review || {}), reviewed: false });
    } catch (error) {
      logger.error('[BillingReconciliation] Failed to reset variance review:', error);
    }
  }, [onReviewChange, resetVarianceReviews, review, reviewTargetRow, row, runId]);
  const handleRefreshBacContext = useCallback(() => {
    if (!runId || !row?.bac) return;
    setRefreshBaselineFingerprint(currentWarmFingerprint);
    refreshBacContext.mutate({
      runId,
      bac: row.bac,
      varianceId: row.id || row.varianceId || row.variance_id || null,
      importMode,
      contextMode: 'critical',
    }, {
      onSuccess: (result) => {
        setRefreshObservedActive(false);
        setRefreshNotice(result?.enqueued
          ? 'Refresh queued. The drawer will keep showing the current snapshot until the worker finishes warming this BAC.'
          : 'Refresh completed.');
        if (!result?.enqueued) setRefreshBaselineFingerprint(null);
      },
      onError: (error) => {
        setRefreshNotice(error?.response?.data?.message || error?.message || 'Refresh could not be queued.');
        setRefreshBaselineFingerprint(null);
      },
    });
  }, [currentWarmFingerprint, importMode, refreshBacContext, row?.bac, row?.id, row?.varianceId, row?.variance_id, runId]);

  const navigationBlocked = Boolean(
    confirmAction
    || draftVarianceAction
    || draftDpeAction
    || historyEntry
    || productDetailContext
    || recurringPromotionActionId
    || varianceMenuState
  );
  useVarianceDrawerKeyboardShortcuts({
    accountResolverContext,
    canMarkComplete: canMarkCompleteCurrent,
    handleMarkComplete,
    handleOpenAccountResolver,
    hasUnsavedVarianceActions,
    hasVarianceActions,
    isVarianceActionBusy,
    navigationBlocked,
    onNavigateNext: navigation?.onNext,
    onNavigatePrevious: navigation?.onPrevious,
    open,
    requestResetAllVarianceActions,
    safeClose,
    saveAllVarianceActions,
    setProductDetailContext,
    toggleFullscreen,
  });

  if (!row) return null;
  const isMatch = row.status === 'match';
  const isReviewed = Boolean(review?.reviewed);
  const hasReviewHistory = Boolean(review && (review.reviewed || review.category || review.remediation || (Array.isArray(review.notes) && review.notes.length > 0)));
  const showReviewPanels = !isMatch || hasReviewHistory;
  const normalizeWorkflowNote = (note = {}) => ({
    id: note.id,
    text: note.text || '',
    author: note.authorName || note.author_name || note.author || 'Unknown',
    ts: note.createdAt || note.created_at || note.ts || note.updatedAt || note.updated_at,
    updatedAt: note.updatedAt || note.updated_at || note.ts || null,
    status: note.status === 'system' || note.author === 'System' ? 'system' : 'final',
  });
  const workflowNotes = Array.isArray(rowEvidenceQuery.data?.workflow?.notes)
    ? rowEvidenceQuery.data.workflow.notes.map(normalizeWorkflowNote).filter((note) => note.text)
    : [];
  const baseBodyReview = review || nextReviewPayload(review, {});
  const bodyReview = workflowNotes.length && !(Array.isArray(baseBodyReview.notes) && baseBodyReview.notes.length)
    ? { ...baseBodyReview, notes: workflowNotes }
    : baseBodyReview;

  const body = (
    <VarianceDrawerBody
      accountResolverContext={accountResolverContext}
      billingProductFamilyOrder={billingProductFamilyLayout.order}
      billingProductFamilyMinRows={billingProductFamilyLayout.minRows}
      bodyReview={bodyReview}
      canMarkComplete={canMarkCompleteCurrent}
      catalogItems={catalogQuery.data || []}
      confirmAction={confirmAction}
      context={context}
      cpqItems={displayCpqLineItems}
      currentUserName={currentUserName}
      displayTotals={displayTotals}
      drawerMode={drawerMode}
      draftVarianceAction={draftVarianceAction}
      draftDpeAction={draftDpeAction}
      dpeActionError={dpeActionError}
      dpeActions={dpeActions}
      evidencePending={evidencePending}
      evidencePendingReason={evidencePendingReason}
      evidenceRows={displayEvidenceRows}
      fileHealthQuery={mergedFileHealthQuery}
      focusProductCode={focusProductCode}
      gmItems={evidenceRows.gmLineItems || []}
      handleApplyNoteTemplate={handleApplyNoteTemplate}
      handleApplyProductLink={handleApplyProductLink}
      handleConfirmAction={handleConfirmAction}
      handleEditNote={handleEditNote}
      handleDeleteNote={handleDeleteNote}
      handleEvidenceDrop={handleEvidenceDrop}
      handleEvidenceRemoveDrop={handleEvidenceRemoveDrop}
      handleDpeActionDrop={handleDpeActionDrop}
      handleDpeActionRemoveDrop={handleDpeActionRemoveDrop}
      handleMarkComplete={handleMarkComplete}
      handleResetReview={handleResetReview}
      handleMarkReviewed={handleMarkReviewed}
      handleOpenAccountResolver={handleOpenAccountResolver}
      handleOpenVarianceMenu={handleOpenVarianceMenu}
      handlePromoteRecurring={handlePromoteRecurring}
      handleRefreshBacContext={handleRefreshBacContext}
      handleSaveVarianceAction={handleSaveVarianceAction}
      handleSaveDpeAction={handleSaveDpeAction}
      handleSelectVarianceAction={handleSelectVarianceAction}
      hasUnsavedVarianceActions={hasUnsavedVarianceActions}
      hasVarianceActions={hasVarianceActions}
      historyEntry={historyEntry}
      isMatch={isMatch}
      isProductCardDense={false}
      isRefreshingBacContext={refreshBacContext.isPending}
      lifecycleAlerts={bundleLifecycleAlerts}
      isReviewed={isReviewed}
      isVarianceActionBusy={isVarianceActionBusy}
      isVarianceActionSaveBusy={isVarianceActionSaveBusy}
      isDpeActionBusy={isDpeActionBusy}
      isResetReviewBusy={resetVarianceReviews.isPending}
      mockMode={mockMode}
      mockSources={mockSources}
      noteDraft={noteDraft}
      activeDraftNoteId={activeDraftNoteId}
      allowDpeActions={allowDpeActions}
      navigation={navigation}
      productDetailContext={productDetailContext}
      program={row.program || context?.program || 'website'}
      recurringPromotionActionId={recurringPromotionActionId}
      recurringSuggestions={recurringSuggestions}
      recurringSuggestionsQuery={mergedRecurringSuggestionsQuery}
      removeVarianceAction={removeVarianceAction}
      removeDpeAction={removeDpeAction}
      requestResetAllVarianceActions={requestResetAllVarianceActions}
      row={{ ...row, serviceMonth: row.serviceMonth || serviceMonth }}
      rowEvidenceQuery={rowEvidenceQuery}
      runId={runId}
      safeClose={safeClose}
      saveAllVarianceActions={saveAllVarianceActions}
      scheduleNoteSave={scheduleNoteSave}
      sealCurrentDraft={sealCurrentDraft}
      setCategory={setCategory}
      setConfirmAction={setConfirmAction}
      setDraftVarianceAction={setDraftVarianceAction}
      setDraftDpeAction={setDraftDpeAction}
      setHistoryEntry={setHistoryEntry}
      setNoteDraft={setNoteDraft}
      setProductDetailContext={setProductDetailContext}
      setRecurringPromotionActionId={setRecurringPromotionActionId}
      setRemediation={setRemediation}
      setVarianceActionError={setVarianceActionError}
      setVarianceMenuState={setVarianceMenuState}
      setDrawerMode={setDrawerMode}
      showReviewPanels={showReviewPanels}
      submitRecurringPromotion={submitRecurringPromotion}
      varianceActionError={varianceActionError}
      varianceActions={varianceActions}
      varianceMenuState={varianceMenuState}
    />
  );

  return (
    <ResizableDrawerShell
      open={open}
      onClose={safeClose}
      drawerWidth={drawerWidth}
      drawerMode={drawerMode}
      isResizing={isResizing}
      onStartResize={startResize}
      onResetWidth={resetWidth}
    >
      {!mockMode && (
        <Box sx={{ px: 2.5, pt: 1.5, pb: 0.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              {refreshNotice || 'BAC context uses a billing snapshot cache until manually refreshed.'}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CloudSyncOutlinedIcon fontSize="small" />}
              disabled={refreshBacContext.isPending || !runId || !row?.bac}
              onClick={handleRefreshBacContext}
            >
              {refreshBacContext.isPending ? 'Queueing refresh...' : 'Refresh this BAC'}
            </Button>
          </Stack>
        </Box>
      )}
      {body}
    </ResizableDrawerShell>
  );
};

export default VarianceDrawer;
